import React, { useState, useEffect } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
    IconButton, Alert, Chip, CircularProgress, InputAdornment, ButtonBase, Collapse,
} from '@mui/material';
import {
    Close, PhotoCamera, Add, ExpandMore, CheckCircle, RadioButtonUnchecked,
} from '@mui/icons-material';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { formatSchedule } from '../utils/schedule';
import SchedulePicker from './SchedulePicker';
import DayPricesEditor, { cleanDayPrices } from './DayPricesEditor';
import ScheduleChangeConfirm, { daysChanged } from './ScheduleChangeConfirm';

/**
 * Add a product, or change one — the same form for both, so the two never drift apart.
 *
 * One scrolling page of plain questions, each in its own card with a small Telugu hint:
 * photo + name, "How do you charge?", "How do customers get it?", price, delivery days,
 * category. The footer always says what will be saved ("Eenadu · ₹8 per item · Every Sunday")
 * or, until the form is complete, what is still missing.
 *
 * props:
 *   open, onClose
 *   mode            'add' | 'edit'
 *   product         the product being changed (edit)
 *   businessId      (add)
 *   bizMode         'subscription' | 'orders' | 'both'
 *   categoryOptions the vendor's categories, in their Sort & Categories order
 *   onSaved()       after a successful save (the dialog closes itself)
 *   onChangePrice(product)  edit: price has its own history, so it opens the Price dialog
 */

const EVERY_DAY = { f_schedule_type: 'daily', f_schedule_value: 1, f_anchor_date: null };
const MODE_TO_NUM = { sub_only: 1, order_only: 0, both: 2 };
const NUM_TO_MODE = { 1: 'sub_only', 0: 'order_only', 2: 'both' };

const CHARGE_CHOICES = [
    { value: 'PerEachItem', title: 'Per item', example: '₹ for each packet or paper delivered' },
    { value: 'MonthlyFlatFee', title: 'Monthly rate', example: 'Same amount every month, however much is delivered' },
];
const SOLD_CHOICES = [
    { value: 'sub_only', title: 'Regular delivery', example: 'On a plan — e.g. daily milk, newspaper' },
    { value: 'order_only', title: 'Only when ordered', example: 'Customer orders when they need it' },
    { value: 'both', title: 'Both', example: 'On a plan, and also by order' },
];

// Phone photos are several MB; 800px JPEG is plenty for a product card.
const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
        const MAX = 800;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
            if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
            else { width = Math.round(width * MAX / height); height = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Compression failed')), 'image/jpeg', 0.82);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
});

function Section({ title, telugu, children }) {
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, bgcolor: 'background.paper' }}>
            <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={800}>{title}</Typography>
                {telugu && <Typography variant="caption" color="text.disabled">{telugu}</Typography>}
            </Box>
            {children}
        </Box>
    );
}

// A full-width, radio-style choice: big enough to tap, with an example so the words mean something.
function ChoiceList({ choices, value, onChange, disabled }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {choices.map((c) => {
                const on = value === c.value;
                return (
                    <ButtonBase key={c.value} disabled={disabled} onClick={() => onChange(c.value)}
                        sx={{
                            justifyContent: 'flex-start', textAlign: 'left', gap: 1.25, px: 1.25, py: 1, minHeight: 56,
                            borderRadius: 1.5, border: '1.5px solid', borderColor: on ? 'primary.main' : 'divider',
                            bgcolor: on ? 'rgba(25,118,210,0.06)' : 'transparent',
                            opacity: disabled && !on ? 0.45 : 1,
                        }}>
                        {on ? <CheckCircle color="primary" /> : <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />}
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={on ? 800 : 600}>{c.title}</Typography>
                            <Typography variant="caption" color="text.secondary">{c.example}</Typography>
                        </Box>
                    </ButtonBase>
                );
            })}
        </Box>
    );
}

const emptyForm = (bizMode) => ({
    name: '',
    description: '',
    billingType: bizMode === 'orders' ? 'PerEachItem' : '',
    sellingMode: bizMode === 'orders' ? 'order_only' : null,
    price: '',
    dayPrices: {},
    schedule: { ...EVERY_DAY },
    categories: [],
    minQty: 1,
    qtyStep: 1,
});

const fromProduct = (p, bizMode) => ({
    name: p.f_name || '',
    description: p.f_description || '',
    billingType: p.f_billing_type || 'PerEachItem',
    sellingMode: bizMode === 'orders' ? 'order_only' : (NUM_TO_MODE[p.f_selling_mode] ?? null),
    price: '',
    dayPrices: {},
    schedule: {
        f_schedule_type: p.f_schedule_type || 'daily',
        f_schedule_value: p.f_schedule_value ?? 1,
        f_anchor_date: p.f_anchor_date || null,
    },
    categories: (Array.isArray(p.f_categories) && p.f_categories.length)
        ? p.f_categories
        : (p.f_business_product_category ? [p.f_business_product_category] : []),
    minQty: p.f_min_order_qty ?? 1,
    qtyStep: p.f_increment_order_qty ?? 1,
});

const ProductFormDialog = ({
    open, onClose, mode = 'add', product, businessId, bizMode = 'subscription',
    categoryOptions = [], onSaved, onChangePrice,
}) => {
    const isEdit = mode === 'edit';
    const [form, setForm] = useState(() => emptyForm(bizMode));
    const [newCat, setNewCat] = useState(null);         // null = closed; string = typing a new category
    const [moreOpen, setMoreOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [confirmDays, setConfirmDays] = useState(false);
    // Photo: on add it waits locally until the product exists; on edit it uploads straight away.
    const [photoBlob, setPhotoBlob] = useState(null);
    const [photoUrl, setPhotoUrl] = useState(null);
    const [photoBusy, setPhotoBusy] = useState(false);

    // Reset only when the dialog opens or switches product — not when the list reloads after a
    // photo or price change, which hands us a new copy of the same product mid-edit.
    const productId = product?.f_id;
    useEffect(() => {
        if (!open) return;
        setForm(isEdit && product ? fromProduct(product, bizMode) : emptyForm(bizMode));
        setNewCat(null);
        setMoreOpen(false);
        setError('');
        setConfirmDays(false);
        setPhotoBlob(null);
        setPhotoUrl(isEdit && product ? (product.f_image_url || null) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, isEdit, productId, bizMode]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const isPlanRow = Boolean(product?.f_parent_business_product_id);
    const perItem = form.billingType === 'PerEachItem';
    const onPlans = form.sellingMode === 'sub_only' || form.sellingMode === 'both';
    const onOrders = form.sellingMode === 'order_only' || form.sellingMode === 'both';
    // Delivery days only for a per-item product on plans: a monthly rate is the same whichever
    // days you deliver, and an orders-only product has no daily deliveries to skip.
    const showDays = !isPlanRow && bizMode !== 'orders' && perItem && onPlans;
    const noDaysPicked = showDays && form.schedule.f_schedule_type === 'weekly' && !form.schedule.f_schedule_value;
    const dayChange = isEdit && showDays && daysChanged(product, form.schedule);
    const unit = form.billingType === 'MonthlyFlatFee' ? 'per month' : 'per item';

    // ── What is missing, in the order the vendor meets the questions ──
    const missing = !form.name.trim() ? 'Type the product name'
        : !form.billingType ? 'Pick how you charge'
        : bizMode !== 'orders' && !form.sellingMode ? 'Pick how customers get it'
        : !isEdit && !(parseFloat(form.price) > 0) ? 'Type the price'
        : noDaysPicked ? 'Pick at least one delivery day'
        : '';

    const shownPrice = isEdit
        ? (product?.current_price ? `₹${product.current_price}` : '')
        : (parseFloat(form.price) > 0 ? `₹${parseFloat(form.price)}` : '');
    const summary = [
        form.name.trim(),
        shownPrice && `${shownPrice} ${unit}`,
        showDays && formatSchedule(form.schedule) !== 'Every day' ? formatSchedule(form.schedule) : null,
    ].filter(Boolean).join(' · ');

    // ── Photo ──
    const pickPhoto = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setError('');
        try {
            const blob = await compressImage(file);
            if (!isEdit) {
                if (photoUrl) URL.revokeObjectURL(photoUrl);
                setPhotoBlob(blob);
                setPhotoUrl(URL.createObjectURL(blob));
                return;
            }
            setPhotoBusy(true);
            const res = await businessAPI.uploadProductImage(product.f_id, new File([blob], 'image.jpg', { type: 'image/jpeg' }));
            setPhotoUrl(res.data.f_image_url);
            onSaved && onSaved();
        } catch {
            setError('Photo could not be added. Please try again.');
        } finally {
            setPhotoBusy(false);
        }
    };

    const removePhoto = async () => {
        if (!isEdit) {
            if (photoUrl) URL.revokeObjectURL(photoUrl);
            setPhotoBlob(null);
            setPhotoUrl(null);
            return;
        }
        setPhotoBusy(true);
        try {
            await businessAPI.deleteProductImage(product.f_id);
            setPhotoUrl(null);
            onSaved && onSaved();
        } catch {
            setError('Photo could not be removed. Please try again.');
        } finally {
            setPhotoBusy(false);
        }
    };

    // ── Categories: tap the vendor's own, or add a new one ──
    const toggleCat = (c) => set({
        categories: form.categories.includes(c)
            ? form.categories.filter((x) => x !== c)
            : [...form.categories, c].slice(0, 10),
    });
    const addNewCat = () => {
        const c = (newCat || '').trim().slice(0, 50);
        if (c && !form.categories.some((x) => x.toLowerCase() === c.toLowerCase())) {
            set({ categories: [...form.categories, c].slice(0, 10) });
        }
        setNewCat(null);
    };
    // A category still in the box when Save is pressed counts too.
    const finalCategories = () => {
        const c = (newCat || '').trim().slice(0, 50);
        return (c && !form.categories.some((x) => x.toLowerCase() === c.toLowerCase()))
            ? [...form.categories, c].slice(0, 10) : form.categories;
    };
    const allCats = [...categoryOptions, ...form.categories.filter((c) => !categoryOptions.includes(c))];

    // ── Save ──
    const scheduleFields = () => ({
        f_schedule_type: form.schedule.f_schedule_type,
        f_schedule_value: form.schedule.f_schedule_value,
        f_anchor_date: form.schedule.f_schedule_type === 'interval' ? form.schedule.f_anchor_date : null,
    });

    const handleSaveClick = () => {
        if (missing) return;
        if (dayChange && (product.active_customer_count || 0) > 0) setConfirmDays(true);
        else save();
    };

    const save = async () => {
        setConfirmDays(false);
        setSaving(true);
        setError('');
        try {
            const sellingMode = bizMode === 'orders' ? 0 : MODE_TO_NUM[form.sellingMode];
            if (isEdit) {
                await businessAPI.updateBusinessProduct(product.f_id, {
                    f_name: form.name.trim(),
                    f_description: form.description,
                    f_billing_type: form.billingType,
                    f_categories: finalCategories(),
                    f_selling_mode: sellingMode,
                    f_min_order_qty: parseFloat(form.minQty) || 1,
                    f_increment_order_qty: parseFloat(form.qtyStep) || 1,
                    ...(dayChange ? scheduleFields() : {}),
                });
            } else {
                const dayPrices = perItem ? cleanDayPrices(form.dayPrices) : {};
                const res = await businessAPI.addProductToBusiness({
                    f_business_id: businessId,
                    f_product_id: null,
                    f_name: form.name.trim(),
                    f_description: form.description.trim() || undefined,
                    f_billing_type: form.billingType,
                    initial_price: parseFloat(form.price),
                    f_is_available: true,
                    f_selling_mode: sellingMode,
                    f_categories: finalCategories(),
                    day_prices: Object.keys(dayPrices).length ? dayPrices : undefined,
                    ...(showDays ? scheduleFields() : {}),
                });
                if (photoBlob && res?.data?.f_id) {
                    try {
                        await businessAPI.uploadProductImage(res.data.f_id, new File([photoBlob], 'image.jpg', { type: 'image/jpeg' }));
                    } catch { /* the product is saved; a photo can be added later */ }
                }
                if (photoUrl) URL.revokeObjectURL(photoUrl);
            }
            onSaved && onSaved();
            onClose();
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(Array.isArray(d) ? d.map((e) => e.msg).join(', ') : typeof d === 'string' ? d : 'Could not save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    if (isEdit && !product) return null;
    const billingLocked = isEdit;   // every product has a price from day one, and the type can't change after that

    return (
        <>
        <Dialog open={open} onClose={onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" noWrap sx={{ flex: 1, pr: 1 }}>
                    {isEdit ? 'Change product' : 'Add Product'}
                </Typography>
                <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ px: 1.5, py: 1.5, bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>

                    {/* ── Photo + name: reads like the product card it will become ── */}
                    <Section title="Product" telugu="ప్రొడక్ట్ పేరు, ఫోటో">
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                            <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                                <Box component="label" sx={{
                                    width: 88, height: 88, borderRadius: 2, overflow: 'hidden', cursor: 'pointer',
                                    border: photoUrl ? '1px solid' : '1.5px dashed', borderColor: photoUrl ? 'divider' : 'grey.400',
                                    bgcolor: 'grey.50', display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center', gap: 0.25,
                                }}>
                                    {photoBusy ? <CircularProgress size={24} />
                                        : photoUrl
                                            ? <Box component="img" src={photoUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            : <>
                                                <PhotoCamera sx={{ color: 'text.disabled' }} />
                                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1 }}>Add photo</Typography>
                                            </>}
                                    {/* No `capture`: that forces the camera and hides the gallery. */}
                                    <input type="file" accept="image/*" hidden onChange={pickPhoto} disabled={photoBusy} />
                                </Box>
                                {photoUrl && !photoBusy && (
                                    <Button size="small" color="error" onClick={removePhoto}
                                        sx={{ textTransform: 'none', minWidth: 0, p: 0.25, mt: 0.25, fontSize: '0.75rem' }}>
                                        Remove
                                    </Button>
                                )}
                            </Box>
                            <TextField fullWidth autoFocus={!isEdit} label="Product name"
                                placeholder="e.g. Eenadu, Amul Milk 500ml"
                                value={form.name} autoComplete="off"
                                onChange={(e) => set({ name: e.target.value })}
                                inputProps={{ maxLength: 255 }} />
                        </Box>
                    </Section>

                    {/* ── How do you charge? ── */}
                    {!isPlanRow && (
                        <Section title="How do you charge?" telugu="ఎలా చార్జ్ చేస్తారు?">
                            <ChoiceList choices={CHARGE_CHOICES} value={form.billingType} disabled={billingLocked}
                                onChange={(v) => set({ billingType: v, dayPrices: {} })} />
                            {billingLocked && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                    This can't be changed once a price is set.
                                </Typography>
                            )}
                        </Section>
                    )}

                    {/* ── How do customers get it? ── */}
                    {bizMode !== 'orders' && !isPlanRow && (
                        <Section title="How do customers get it?" telugu="కస్టమర్లకు ఎలా ఇస్తారు?">
                            <ChoiceList choices={SOLD_CHOICES} value={form.sellingMode}
                                onChange={(v) => set({ sellingMode: v })} />
                        </Section>
                    )}

                    {/* ── Price ── */}
                    {!isPlanRow && (
                        <Section title="Price" telugu="ధర">
                            {isEdit ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6" fontWeight={800} sx={{ flex: 1 }}>
                                        {product.current_price ? `₹${product.current_price}` : 'No price yet'}
                                        <Typography component="span" variant="body2" color="text.secondary"> {unit}</Typography>
                                    </Typography>
                                    {onChangePrice && (
                                        <Button variant="outlined" onClick={() => onChangePrice(product)}
                                            sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }}>
                                            Change price
                                        </Button>
                                    )}
                                </Box>
                            ) : (
                                <>
                                    <TextField fullWidth type="number" placeholder="0"
                                        value={form.price}
                                        onChange={(e) => set({ price: e.target.value })}
                                        InputProps={{
                                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                                            endAdornment: form.billingType
                                                ? <InputAdornment position="end">{unit}</InputAdornment> : null,
                                            sx: { fontSize: '1.25rem', fontWeight: 700 },
                                        }}
                                        inputProps={{ min: 0, step: 0.5, inputMode: 'decimal', 'aria-label': 'Price' }} />
                                    {perItem && (
                                        <Box sx={{ mt: 1.25 }}>
                                            <DayPricesEditor value={form.dayPrices} basePrice={form.price}
                                                onChange={(dp) => set({ dayPrices: dp })} />
                                        </Box>
                                    )}
                                </>
                            )}
                        </Section>
                    )}

                    {/* ── Delivery days ── */}
                    {showDays && (
                        <Section title="Delivery days" telugu="ఏ రోజుల్లో డెలివరీ?">
                            <SchedulePicker value={form.schedule} onChange={(sch) => set({ schedule: sch })} />
                            {dayChange && (product.active_customer_count || 0) > 0 && (
                                <Typography variant="caption" color="warning.dark" sx={{ display: 'block', mt: 1 }}>
                                    {product.active_customer_count} {product.active_customer_count === 1 ? 'customer is' : 'customers are'} on
                                    this product. The new days apply to all of them.
                                </Typography>
                            )}
                        </Section>
                    )}

                    {/* ── Category ── */}
                    <Section title="Category (optional)" telugu="కేటగిరీ">
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            {allCats.map((c) => {
                                const on = form.categories.includes(c);
                                return (
                                    <Chip key={c} label={c} onClick={() => toggleCat(c)}
                                        color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'}
                                        sx={{ height: 36, fontWeight: on ? 700 : 500 }} />
                                );
                            })}
                            {newCat === null && (
                                <Chip icon={<Add />} label="New" variant="outlined" onClick={() => setNewCat('')}
                                    sx={{ height: 36, borderStyle: 'dashed' }} />
                            )}
                        </Box>
                        {newCat !== null && (
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <TextField size="small" fullWidth autoFocus placeholder="e.g. Papers, Milk"
                                    value={newCat} onChange={(e) => setNewCat(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNewCat(); } }}
                                    inputProps={{ maxLength: 50 }} />
                                <Button variant="contained" disableElevation onClick={addNewCat}
                                    sx={{ minHeight: 40, textTransform: 'none', fontWeight: 700 }}>Add</Button>
                            </Box>
                        )}
                    </Section>

                    {/* ── Rarely needed: description, order quantities ── */}
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                        <ButtonBase onClick={() => setMoreOpen((o) => !o)}
                            sx={{ width: '100%', minHeight: 48, px: 1.5, justifyContent: 'space-between' }}>
                            <Typography variant="body2" fontWeight={700} color="text.secondary">More details (optional)</Typography>
                            <ExpandMore sx={{ color: 'text.secondary', transform: moreOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
                        </ButtonBase>
                        <Collapse in={moreOpen}>
                            <Box sx={{ px: 1.5, pb: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField fullWidth multiline minRows={2} label="Description"
                                    value={form.description} onChange={(e) => set({ description: e.target.value })} />
                                {isEdit && onOrders && (
                                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                                        <TextField sx={{ flex: 1 }} type="number" label="Smallest order"
                                            value={form.minQty} onChange={(e) => set({ minQty: e.target.value })}
                                            inputProps={{ min: 0.5, step: 0.5, inputMode: 'decimal' }} />
                                        <TextField sx={{ flex: 1 }} type="number" label="+ / − by"
                                            value={form.qtyStep} onChange={(e) => set({ qtyStep: e.target.value })}
                                            inputProps={{ min: 0.5, step: 0.5, inputMode: 'decimal' }} />
                                    </Box>
                                )}
                            </Box>
                        </Collapse>
                    </Box>

                    {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
                </Box>
            </DialogContent>

            {/* ── Footer: what will be saved, or what is still missing ── */}
            <DialogActions sx={{ px: 2, py: 1.25, borderTop: '1px solid', borderColor: 'divider', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                <Typography variant="body2" noWrap sx={{
                    textAlign: 'center', fontWeight: 700,
                    color: missing ? 'warning.dark' : 'text.primary',
                }}>
                    {missing ? `Next: ${missing}` : summary}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, m: '0 !important' }}>
                    <Button variant="outlined" onClick={onClose} sx={{ minHeight: 48, textTransform: 'none' }}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={handleSaveClick}
                        disabled={saving || photoBusy || Boolean(missing)}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ flex: 1, minHeight: 48, textTransform: 'none', fontWeight: 800, fontSize: '1rem' }}>
                        {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Save Product'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>

        {isEdit && (
            <ScheduleChangeConfirm
                open={confirmDays}
                schedule={form.schedule}
                customerCount={product.active_customer_count}
                kind="product"
                onCancel={() => setConfirmDays(false)}
                onConfirm={save}
            />
        )}
        </>
    );
};

export default ProductFormDialog;
